import {useMemo} from "react"
import {useQuery} from "@tanstack/react-query"
import {find, get, isEmpty} from "lodash"
import {getFullName, handleError} from "helpers"
import {useModel} from "hooks/useModel"
import {calendarService} from "services"
import {LinkedCalendarInfo, MyCalendar} from "types/calendar"
import {Auth} from "types/auth"

type UseMyLinkedCalendarsOptions = {
    viewOtherUserId?: number
    primaryUser?: Auth.UserProfile
}

const EMPTY_CALENDARS: MyCalendar[] = []

export const mapMyCalendarsToLinkedCalendars = (
    data: MyCalendar[],
    primaryUser: Auth.UserProfile
): LinkedCalendarInfo[] => {
    return data
        .filter((item) => item.is_primary || !isEmpty(item.integration_data) || item.type === "group")
        .map((item) => ({
            isPrimary: item.is_primary,
            type: item.type,
            id: item.type === "group" ? item.calendar_id : !item.is_primary ? get(item, "integration_data.id") : null,
            calendarId: item.calendar_id,
            name: item.is_primary ? getFullName(primaryUser) : item.name,
            provider: !item.is_primary ? get(item, "integration_data.account.provider") : null,
            requiresReconnect: !item.is_primary && !!get(item, "integration_data.requiresReconnect"),
            color: get(item, "integration_data.color", null),
            accountId: get(item, "integration_data.account.id", null),
            accountName: get(item, "integration_data.account.name", null),
            isShow: item.is_primary || !get(item, "integration_data.requiresReconnect")
        }))
}

export const useMyLinkedCalendars = (options?: UseMyLinkedCalendarsOptions) => {
    const model = useModel()
    const viewOtherUserId = options?.viewOtherUserId
    const primaryUser = options?.primaryUser ?? model.user
    const queryKey = viewOtherUserId != null ? ["my-linked-calendars", viewOtherUserId] : ["my-linked-calendars"]

    const query = useQuery(
        queryKey,
        () => calendarService.getMyCalendars(viewOtherUserId != null ? {viewOtherUserId} : undefined),
        {onError: (error) => handleError(error)}
    )

    const myCalendars = query.data ?? EMPTY_CALENDARS

    const linkedCalendars = useMemo(
        () => mapMyCalendarsToLinkedCalendars(myCalendars, primaryUser),
        [myCalendars, primaryUser]
    )

    const primaryCalendarId = useMemo(() => find(myCalendars, (item) => item.is_primary)?.calendar_id, [myCalendars])

    return {
        data: linkedCalendars,
        linkedCalendars,
        myCalendars,
        primaryCalendarId,
        isLoading: query.isLoading,
        isFetching: query.isFetching
    }
}
